import { extractDddFromPhone } from '../services/utils/stringUtils';
import {
  getFastchipClusterSettings,
  resolveRjMgEsActiveCluster,
  formatClusterDddsList,
  formatClusterTokenLabel,
  formatClusterUfs,
  formatBaTokenLabel,
  formatDhTokenLabel,
  formatEr2FastchipTokenLabel,
  formatHdm2TokenLabel,
  formatTpclTokenLabel,
  isBaMarketEstado,
  isBaDdd,
  isDhMarketEstado,
  isHdm2MarketEstado,
  isHdm2Ddd,
  isCpMarketEstado,
  isEr2MarketEstado,
  isHbMarketEstado,
  isTpclDdd,
  isTpclEntregaMarketEstado,
  isTpclMarketEstado,
  isPrMhClusterCity,
  LEGACY_NON_FASTCHIP_TPCL_TOKEN,
  LEGACY_NON_FASTCHIP_TPCL_TOKEN_V2,
  NON_FASTCHIP_DH_TOKEN_VALUE,
  NON_FASTCHIP_ER2_SP_TOKEN_VALUE,
  NON_FASTCHIP_TPCL_TOKEN_VALUE,
  type SeniorClusterHbCp,
} from './fastchipClusterRules';

const ER2_FASTCHIP_TOKEN_VALUE = 'E.R.2 GESTAO';

/** Token Senior activo para Fastchip RJ/MG/ES (espelha API `ResolveRjMgEsFastchipSeniorToken`). */
export function resolveRjMgEsFastchipSeniorToken(
  teamClusterPreference?: 'ER2' | 'DH' | null
): 'E.R.2 GESTAO' | 'DH' {
  return resolveRjMgEsActiveCluster(teamClusterPreference) === 'DH' ? 'DH' : ER2_FASTCHIP_TOKEN_VALUE;
}

export { formatClusterTokenLabel } from './fastchipClusterRules';

/**
 * Tokens de cluster alinhados a `ContractSaleTokenHelper` / `SeniorAccountHelper.GetClusterIniciais`.
 * Valores devem coincidir exactamente com os canónicos persistidos na API quando `fastchip === true`.
 *
 * Quando `fastchip === false`, a lista operacional TIM (rótulos por região/DDD) é a fonte de valores
 * explícitos aceites pelo formulário — ver `CONTRACT_CLUSTER_NON_FASTCHIP_OPTIONS`.
 */
export const CONTRACT_CLUSTER_CANONICAL_TOKENS = [
  'SH',
  'HDM',
  'E.R.2 GESTAO',
  'MH',
  'E.R.GESTAO',
  'HB',
  'CP',
  'TPCL',
  'BA',
  'HDM2',
  'DH',
] as const;

export type ContractClusterCanonicalToken = (typeof CONTRACT_CLUSTER_CANONICAL_TOKENS)[number];

const CANONICAL_SET: ReadonlySet<string> = new Set(CONTRACT_CLUSTER_CANONICAL_TOKENS);

/** Todas as opções operacionais (inclui ER2 legado para hydrate / edição). */
const CONTRACT_CLUSTER_NON_FASTCHIP_OPTIONS_ALL: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'MH - PR', label: 'MH - PR — DDD 42' },
  { value: 'ER - PR', label: 'ER - PR — DDD 41, 43, 44, 45' },
  { value: 'HDM - SC', label: 'HDM - SC — DDD 47, 48, 49' },
  { value: 'HDM2 - RS', label: 'HDM2 - RS — DDD 51, 52, 53, 54, 55' },
  { value: 'SH - SP', label: 'SH - SP — DDD 11 a 19 (legado, só edição)' },
  { value: NON_FASTCHIP_ER2_SP_TOKEN_VALUE, label: 'ER2 - SP — São Paulo — DDD 11 a 19' },
  {
    value: 'ER2 - RJ/EP/MG',
    label: 'ER2 — RJ / EP / MG — DDD 21, 22, 24, 27, 28 e 31 a 38 (legado)',
  },
  {
    value: NON_FASTCHIP_DH_TOKEN_VALUE,
    label: formatDhTokenLabel('operational'),
  },
  { value: 'SIX - CE / PB / RN', label: 'SIX — CE / PB / RN — DDD 83, 84, 85, 86, 88' },
  { value: 'CP - PI', label: formatClusterTokenLabel('CP', 'operational') },
  { value: 'HB - AL / PE', label: formatClusterTokenLabel('HB', 'operational') },
  { value: 'BA - BA / SE', label: 'BA — BA / SE — DDD 71, 73, 74, 75, 77, 79' },
  {
    value: 'AMK - AL / PE',
    label: `AMK — AL / PE — DDD ${formatClusterDddsList('HB')}`,
  },
  { value: 'NK - YESS', label: 'NK — YESS' },
  {
    value: 'UB - BE / MA / PA / AP / RR / AM',
    label: 'UB — BE / MA / PA / AP / RR / AM — DDD 91 a 99',
  },
  {
    value: NON_FASTCHIP_TPCL_TOKEN_VALUE,
    label: formatTpclTokenLabel('operational'),
  },
  { value: 'RJ6', label: 'RJ6' },
];

/**
 * Opções do select quando o contrato não é Fastchip, filtradas pela configuração ativa.
 * Com `er2Enabled=false`: oculta ER2 operacional legado (criação nova usa DH).
 */
export function getContractClusterNonFastchipOptions(): ReadonlyArray<{ value: string; label: string }> {
  const { er2Enabled } = getFastchipClusterSettings();
  return CONTRACT_CLUSTER_NON_FASTCHIP_OPTIONS_ALL.filter((o) => {
    if (o.value === 'SH - SP') return false;
    if ((o.value === 'ER2 - RJ/EP/MG' || o.value === NON_FASTCHIP_ER2_SP_TOKEN_VALUE) && !er2Enabled)
      return false;
    return true;
  });
}


const NON_FASTCHIP_VALUE_SET: ReadonlySet<string> = new Set(
  CONTRACT_CLUSTER_NON_FASTCHIP_OPTIONS_ALL.map((o) => o.value)
);

/** Todas as opções Fastchip (inclui DH/E.R.2 para hydrate e reativação via flags). */
const CONTRACT_CLUSTER_FASTCHIP_OPTIONS_ALL: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'SH', label: 'SH — DDD 11 a 19 (SP legado, só edição)' },
  { value: 'HDM', label: 'HDM — DDD 47, 48, 49 (SC)' },
  { value: ER2_FASTCHIP_TOKEN_VALUE, label: formatEr2FastchipTokenLabel() },
  { value: 'MH', label: 'MH — DDD 42 (PR)' },
  { value: 'E.R.GESTAO', label: 'E.R.GESTAO — DDD 41, 43, 44, 45 (PR)' },
  { value: 'HB', label: formatClusterTokenLabel('HB', 'fastchip') },
  { value: 'CP', label: formatClusterTokenLabel('CP', 'fastchip') },
  {
    value: 'TPCL',
    label: formatTpclTokenLabel('fastchip'),
  },
  {
    value: 'BA',
    label: formatBaTokenLabel('fastchip'),
  },
  {
    value: 'HDM2',
    label: formatHdm2TokenLabel('fastchip'),
  },
  {
    value: 'DH',
    label: formatDhTokenLabel('fastchip'),
  },
];

/**
 * Rótulos do select Fastchip, filtrados pela configuração ativa (`useFastchipClusterSettings`).
 * Com `dhEnabled=false`: oculta DH (criação nova usa E.R.2); legado DH hidrata via CANONICAL_SET.
 * Com `er2Enabled=false`: oculta E.R.2.
 */
export function getContractClusterFastchipOptions(): ReadonlyArray<{ value: string; label: string }> {
  const { er2Enabled, dhEnabled } = getFastchipClusterSettings();
  return CONTRACT_CLUSTER_FASTCHIP_OPTIONS_ALL.filter((o) => {
    if (o.value === 'SH') return false;
    if (o.value === ER2_FASTCHIP_TOKEN_VALUE && !er2Enabled) return false;
    if (o.value === 'DH' && !dhEnabled) return false;
    return true;
  });
}


const SP_DDDS = new Set(['11', '12', '13', '14', '15', '16', '17', '18', '19']);
const SC_DDDS = new Set(['47', '48', '49']);
const ER2_DDDS = new Set(['21', '22', '24', '27', '28', '31', '32', '33', '34', '35', '37', '38']);

/** Opções canónicas do filtro de lista (API expande para fastchip + operacional). */
export type ContractListClusterFilterOption = {
  key: string;
  value: string;
  text: string;
};

const CONTRACT_LIST_CLUSTER_FILTER_CANONICAL: ReadonlyArray<{
  value: string;
  text: string;
  group: string;
}> = [
  { value: 'HB', text: `HB — ${formatClusterUfs('HB')}`, group: 'Nordeste' },
  { value: 'CP', text: `CP — ${formatClusterUfs('CP')}`, group: 'Nordeste' },
  { value: 'TPCL', text: 'TPCL — Centro-Oeste e Norte (entrega)', group: 'Centro-Oeste / Norte' },
  { value: 'BA', text: 'BA — Bahia (Fastchip)', group: 'Nordeste' },
  { value: 'HDM2', text: 'HDM2 — Rio Grande do Sul (Fastchip)', group: 'Sul / Sudeste' },
  { value: 'ER', text: 'ER — Paraná (E.R.GESTAO)', group: 'Sul / Sudeste' },
  { value: 'MH', text: 'MH — Paraná', group: 'Sul / Sudeste' },
  { value: 'HDM', text: 'HDM — Santa Catarina', group: 'Sul / Sudeste' },
  { value: 'SH', text: 'SH — São Paulo (legado)', group: 'Sul / Sudeste' },
  { value: 'ER2', text: 'E.R.2 — São Paulo', group: 'Sul / Sudeste' },
  { value: 'DH', text: 'DH — RJ / MG / ES (por entrega)', group: 'Sul / Sudeste' },
  {
    value: 'OUTROS_TIM',
    text: 'Outros TIM — SIX, AMK, UB, NK, RJ6',
    group: 'Outros',
  },
];

/** ~9 opções canónicas (antes: união fastchip + operacional ≈ 23 valores). */
export function getContractListClusterFilterOptions(): ReadonlyArray<ContractListClusterFilterOption> {
  return CONTRACT_LIST_CLUSTER_FILTER_CANONICAL.map((item) => ({
    key: item.value,
    value: item.value,
    text: `${item.group} — ${item.text}`,
  }));
}

/**
 * Opções do select de token.
 * Com flags a ocultar DH/E.R.2, `currentToken` legado (ainda canónico/operacional) volta a aparecer
 * só para edição — criação nova continua sem essa opção.
 */
export function getContractClusterSaleTokenSelectOptions(
  fastchip: boolean,
  currentToken?: string | null
): ReadonlyArray<{ value: string; label: string }> {
  const enabledOptions = fastchip
    ? getContractClusterFastchipOptions()
    : getContractClusterNonFastchipOptions();
  const all = fastchip
    ? CONTRACT_CLUSTER_FASTCHIP_OPTIONS_ALL
    : CONTRACT_CLUSTER_NON_FASTCHIP_OPTIONS_ALL;

  const trimmed = (currentToken ?? '').trim();
  if (!trimmed || enabledOptions.some((o) => o.value === trimmed)) {
    return enabledOptions;
  }

  const legacy = all.find((o) => o.value === trimmed);
  if (!legacy) {
    return enabledOptions;
  }

  return [...enabledOptions, legacy];
}

/** Resolve token persistido (Senior ou operacional TIM) para cluster HB/CP na exibição. */
export function resolveHbCpClusterFromContractToken(
  token: string | undefined | null
): SeniorClusterHbCp | null {
  const u = (token ?? '').trim().toUpperCase();
  if (!u) return null;
  if (u === 'HB' || u.startsWith('HB ') || u.startsWith('HB-')) return 'HB';
  if (u === 'CP' || u.startsWith('CP ') || u.startsWith('CP-')) return 'CP';
  return null;
}

function isTpclContractToken(token: string): boolean {
  const u = token.trim().toUpperCase();
  return u === 'TPCL' || u.startsWith('TPCL ') || u.startsWith('TPCL-');
}

function isBaContractToken(token: string): boolean {
  const u = token.trim().toUpperCase();
  return u === 'BA' || u.startsWith('BA ') || u.startsWith('BA-') || u === 'BAHIA';
}

function isHdm2ContractToken(token: string): boolean {
  const u = token.trim().toUpperCase();
  return u === 'HDM2' || u.startsWith('HDM2 ') || u.startsWith('HDM2-');
}

/** Token canónico `DH` ou operacional `DH - RJ/MG/ES` (e variantes com prefixo DH). */
export function isDhContractToken(token: string | undefined | null): boolean {
  const u = (token ?? '').trim().toUpperCase();
  return u === 'DH' || u.startsWith('DH ') || u.startsWith('DH-');
}

/** Cabeçalho/modal: token + UFs (+ DDDs quando HB/CP, TPCL, BA, HDM2 ou DH Fastchip). */
export function formatContractTokenDisplayLabel(
  token: string | undefined | null,
  fastchip?: boolean
): string {
  const t = (token ?? '').trim();
  if (!t) return '';
  if (isDhContractToken(t)) {
    return fastchip || t.toUpperCase() === 'DH'
      ? formatDhTokenLabel('fastchip')
      : formatDhTokenLabel('operational');
  }
  if (isHdm2ContractToken(t)) {
    return fastchip || t.toUpperCase() === 'HDM2'
      ? formatHdm2TokenLabel('fastchip')
      : formatHdm2TokenLabel('operational');
  }
  if (isBaContractToken(t)) {
    return fastchip || t.toUpperCase() === 'BA' ? formatBaTokenLabel('fastchip') : formatBaTokenLabel('operational');
  }
  if (isTpclContractToken(t)) {
    if (fastchip || t.toUpperCase() === 'TPCL') {
      return formatTpclTokenLabel('fastchip');
    }
    return formatTpclTokenLabel('operational');
  }
  const cluster = resolveHbCpClusterFromContractToken(t);
  if (!cluster) return t;
  return `${t} — ${formatClusterUfs(cluster)} — DDD ${formatClusterDddsList(cluster)}`;
}

/**
 * '' = modo automático (API resolve por região no fluxo Senior quando aplicável).
 * Com `fastchip === true`, só aceita tokens Senior canónicos.
 * Com `fastchip === false`, só aceita tokens da lista operacional TIM (+ alias TPCL legado).
 */
export function normalizeContractSaleTokenForForm(
  raw: string | undefined | null,
  fastchip: boolean
): string {
  const t = (raw ?? '').trim();
  if (!t) return '';
  if (fastchip) {
    return CANONICAL_SET.has(t) ? t : '';
  }
  if (NON_FASTCHIP_VALUE_SET.has(t)) return t;
  if (t === LEGACY_NON_FASTCHIP_TPCL_TOKEN || t === LEGACY_NON_FASTCHIP_TPCL_TOKEN_V2) {
    return NON_FASTCHIP_TPCL_TOKEN_VALUE;
  }
  return '';
}

/**
 * Hidrata o token persistido para o valor exibido no select do formulário.
 * Aceita tokens canónicos Senior, operacionais TIM e conversões cruzadas entre modos.
 */
export function hydrateSaleTokenFromContract(
  raw: string | undefined | null,
  fastchip: boolean
): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';

  const normalized = normalizeContractSaleTokenForForm(trimmed, fastchip);
  if (normalized) return normalized;

  if (fastchip) {
    const upgraded = mapNonFastchipOperationalToSeniorCanonical(trimmed);
    if (upgraded) return upgraded;
    if (CANONICAL_SET.has(trimmed)) return trimmed;
  } else {
    const downgraded = mapSeniorTokenToNonFastchipOperational(trimmed);
    if (downgraded && NON_FASTCHIP_VALUE_SET.has(downgraded)) return downgraded;
    if (NON_FASTCHIP_VALUE_SET.has(trimmed)) return trimmed;
  }

  const optionValues = new Set(
    getContractClusterSaleTokenSelectOptions(fastchip).map((option) => option.value)
  );
  if (optionValues.has(trimmed)) return trimmed;

  return '';
}

/** Token operacional TIM para TPCL quando a UF do cliente/entrega está na malha por entrega. */
export function inferNonFastchipTpclTokenFromClientEstado(
  estado: string | undefined | null
): string | null {
  return isTpclEntregaMarketEstado(estado) ? NON_FASTCHIP_TPCL_TOKEN_VALUE : null;
}

/** Token operacional TIM para Bahia/Sergipe quando a UF do cliente é BA. */
export function inferNonFastchipBaTokenFromClientEstado(
  estado: string | undefined | null
): string | null {
  return isBaMarketEstado(estado) ? 'BA - BA / SE' : null;
}

/** Token operacional TIM para RS quando a UF do cliente é RS. */
export function inferNonFastchipHdm2TokenFromClientEstado(
  estado: string | undefined | null
): string | null {
  return isHdm2MarketEstado(estado) ? 'HDM2 - RS' : null;
}

/** Token operacional TIM para RJ/MG/ES por entrega (DH; legado ER2 permanece na lista). */
export function inferNonFastchipDhTokenFromClientEstado(
  estado: string | undefined | null
): string | null {
  return isDhMarketEstado(estado) ? NON_FASTCHIP_DH_TOKEN_VALUE : null;
}

/** Token operacional TIM para SP por entrega (ER2; SH - SP permanece para hydrate). */
export function inferNonFastchipEr2SpTokenFromClientEstado(
  estado: string | undefined | null
): string | null {
  return isEr2MarketEstado(estado) ? NON_FASTCHIP_ER2_SP_TOKEN_VALUE : null;
}

const PR_ER_DDD = new Set(['41', '43', '44', '45']);

/**
 * PR + linhas Aditivo: DDD 42 → cluster Senior MH; DDD 41/43/44/45 → E.R.GESTAO.
 * Outros DDDs no PR não devolvem inferência (deixar sugestão da API / escolha manual).
 */
export function inferPrSeniorClusterTokenFromAditivoDdd(
  ddd: string | undefined | null,
  cidadeCliente?: string | null
): 'MH' | 'E.R.GESTAO' | 'HB' | 'CP' | 'TPCL' | 'BA' | 'HDM2' | null {
  const fromAll = inferFastchipSeniorClusterTokenFromDdd(ddd, cidadeCliente);
  if (!fromAll) return null;
  if (
    fromAll === 'SH' ||
    fromAll === 'HDM' ||
    fromAll === 'E.R.2 GESTAO' ||
    fromAll === 'DH'
  ) {
    return null;
  }
  return fromAll as 'MH' | 'E.R.GESTAO' | 'HB' | 'CP' | 'TPCL' | 'BA' | 'HDM2';
}

/**
 * DDD (cliente, administrador ou linha Aditivo) → token Senior canónico Fastchip.
 * Espelha `FastchipClusterRules.InferSeniorTokenFromAditivoDdd` + SH/HDM/E.R.2 por DDD.
 */
export function inferFastchipSeniorClusterTokenFromDdd(
  ddd: string | undefined | null,
  cidadeCliente?: string | null,
  teamClusterPreference?: 'ER2' | 'DH' | null
): ContractClusterCanonicalToken | null {
  const d = (ddd ?? '').trim();
  if (!d) return null;
  if (d === '81' || d === '87' || d === '85' || d === '88') return 'HB';
  if (d === '86' || d === '89' || d === '82' || d === '84' || d === '83') return 'CP';
  if (isTpclDdd(d)) return 'TPCL';
  if (isBaDdd(d)) return 'BA';
  if (isHdm2Ddd(d)) return 'HDM2';
  if (d === '42') return isPrMhClusterCity(cidadeCliente) ? 'MH' : 'E.R.GESTAO';
  if (PR_ER_DDD.has(d)) return 'E.R.GESTAO';
  if (SP_DDDS.has(d)) return 'E.R.2 GESTAO';
  if (SC_DDDS.has(d)) return 'HDM';
  if (ER2_DDDS.has(d)) return 'DH';
  return null;
}

export type FastchipClusterInferenceLine = {
  negociacao?: string;
  excluirDoContrato?: boolean;
  ddd?: string;
};

/**
 * Prioridade: 1ª linha Aditivo com DDD → telefone do administrador → telefones do cliente → DDD da linha em edição.
 */
export function resolveDddForFastchipClusterInference(input: {
  linhas?: FastchipClusterInferenceLine[];
  linhaAtualDdd?: string;
  adminTelefone1?: string;
  adminTelefone2?: string;
  clientTelefone1?: string;
  clientTelefone2?: string;
}): string | null {
  const firstAditivo = input.linhas?.find(
    (l) => l.negociacao === 'Aditivo' && !l.excluirDoContrato && (l.ddd ?? '').trim()
  );
  if (firstAditivo?.ddd?.trim()) return firstAditivo.ddd.trim();

  const fromPhone = (raw?: string): string | null => {
    if (!raw?.trim()) return null;
    const { ddd } = extractDddFromPhone(raw.trim());
    return ddd?.trim() || null;
  };

  for (const candidate of [
    fromPhone(input.adminTelefone1),
    fromPhone(input.clientTelefone1),
    fromPhone(input.clientTelefone2),
    fromPhone(input.adminTelefone2),
    (input.linhaAtualDdd ?? '').trim() || null,
  ]) {
    if (candidate) return candidate;
  }
  return null;
}

/** Mercado HB/CP/TPCL/BA/HDM2/E.R.2 (ou DH se `DH_FASTCHIP_ENABLED`): token Senior pela UF do cliente. */
export function inferFastchipSeniorClusterTokenFromClientEstado(
  estado: string | undefined | null,
  teamClusterPreference?: 'ER2' | 'DH' | null
): 'HB' | 'CP' | 'TPCL' | 'BA' | 'HDM2' | 'DH' | 'E.R.2 GESTAO' | null {
  if (isEr2MarketEstado(estado)) return 'E.R.2 GESTAO';
  if (isDhMarketEstado(estado)) return 'DH';
  if (isHbMarketEstado(estado)) return 'HB';
  if (isCpMarketEstado(estado)) return 'CP';
  if (isTpclMarketEstado(estado)) return 'TPCL';
  if (isBaMarketEstado(estado)) return 'BA';
  if (isHdm2MarketEstado(estado)) return 'HDM2';
  return null;
}

export type ResolveFastchipClusterTokenInput = {
  linhas?: FastchipClusterInferenceLine[];
  linhaAtualDdd?: string;
  adminTelefone1?: string;
  adminTelefone2?: string;
  clientTelefone1?: string;
  clientTelefone2?: string;
  clientEstado?: string | null;
  apiSuggestion?: string | null;
  /** Valor actual do select (p.ex. token operacional TIM antes de marcar Fastchip). */
  currentFormToken?: string | null;
  /** Preferência de cluster RJ/MG/ES do time do vendedor logado (null = sem preferência). */
  teamClusterPreference?: 'ER2' | 'DH' | null;
};

export type ClusterSuggestionSource =
  | 'UF cadastro'
  | 'linha Aditivo'
  | 'DDD cliente'
  | 'API Senior'
  | 'manual';

export type SuggestedClusterBanner = {
  token: string;
  source: ClusterSuggestionSource | '';
};

/**
 * Texto do painel "Cluster sugerido": API Senior quando responde;
 * senão o token já inferido por UF/DDD (ex.: SP → E.R.2 GESTAO sem linha).
 */
export function resolveSuggestedClusterBanner(input: {
  apiSuggestion?: string | null;
  localToken?: string | null;
  localSource?: ClusterSuggestionSource | string;
}): SuggestedClusterBanner {
  const api = (input.apiSuggestion ?? '').trim();
  if (api) return { token: api, source: 'API Senior' };
  const local = (input.localToken ?? '').trim();
  if (local) {
    const src = (input.localSource ?? 'UF cadastro').trim();
    if (src === 'API Senior' || src === 'UF cadastro' || src === 'linha Aditivo' || src === 'DDD cliente') {
      return { token: local, source: src };
    }
    return { token: local, source: 'UF cadastro' };
  }
  return { token: '', source: '' };
}

/** HB/CP operacional TIM a partir da UF de cadastro (mercado HB/CP). */
export function inferNonFastchipHbCpTokenFromClientEstado(
  estado: string | undefined | null
): string | null {
  if (isHbMarketEstado(estado)) return 'HB - AL / PE';
  if (isCpMarketEstado(estado)) return 'CP - PI';
  return null;
}

/** Mapeia token Senior canónico para valor operacional TIM no select non-fastchip. */
export function mapSeniorTokenToNonFastchipOperational(
  senior: ContractClusterCanonicalToken | string | null | undefined
): string | null {
  if (!senior) return null;
  const s = String(senior).trim().toUpperCase();
  if (s === 'HB') return 'HB - AL / PE';
  if (s === 'CP') return 'CP - PI';
  if (s === 'TPCL') return NON_FASTCHIP_TPCL_TOKEN_VALUE;
  if (s === 'BA') return 'BA - BA / SE';
  if (s === 'HDM2') return 'HDM2 - RS';
  if (s === 'MH') return 'MH - PR';
  if (s === 'E.R.GESTAO') return 'ER - PR';
  if (s === 'SH') return 'SH - SP';
  if (s === 'HDM') return 'HDM - SC';
  if (s === 'E.R.2 GESTAO') return NON_FASTCHIP_ER2_SP_TOKEN_VALUE;
  if (s === 'DH') return NON_FASTCHIP_DH_TOKEN_VALUE;
  return null;
}

/** Inverso de `mapSeniorTokenToNonFastchipOperational` — útil ao activar Fastchip com token TIM já preenchido. */
export function mapNonFastchipOperationalToSeniorCanonical(
  operational: string | undefined | null
): ContractClusterCanonicalToken | null {
  const t = (operational ?? '').trim();
  if (!t) return null;
  if (CANONICAL_SET.has(t)) return t as ContractClusterCanonicalToken;
  if (t === LEGACY_NON_FASTCHIP_TPCL_TOKEN || t === LEGACY_NON_FASTCHIP_TPCL_TOKEN_V2) {
    return 'TPCL';
  }
  if (t === 'ER2 - RJ/EP/MG') return 'E.R.2 GESTAO';
  for (const senior of CONTRACT_CLUSTER_CANONICAL_TOKENS) {
    if (mapSeniorTokenToNonFastchipOperational(senior) === t) return senior;
  }
  return null;
}

function inferSeniorFromClientPhones(
  clientTelefone1?: string,
  clientTelefone2?: string
): ContractClusterCanonicalToken | null {
  for (const raw of [clientTelefone1, clientTelefone2]) {
    if (!raw?.trim()) continue;
    const { ddd } = extractDddFromPhone(raw.trim());
    const fromDdd = inferFastchipSeniorClusterTokenFromDdd(ddd?.trim() || null);
    if (fromDdd) return fromDdd;
  }
  return null;
}

export type ResolveNonFastchipClusterTokenInput = {
  clientEstado?: string | null;
  clientTelefone1?: string;
  clientTelefone2?: string;
  apiSuggestion?: string | null;
};

/**
 * Token operacional TIM para preencher o select quando `fastchip === false`.
 * Ordem: UF TPCL/BA/HDM2/DH/HB/CP → DDD telefones cliente → API.
 * Mercado RJ/MG/ES por entrega sugere DH (não o legado ER2).
 */
export function resolveNonFastchipClusterTokenForAutoFill(
  input: ResolveNonFastchipClusterTokenInput
): string {
  const fromTpclUf = inferNonFastchipTpclTokenFromClientEstado(input.clientEstado);
  if (fromTpclUf) return fromTpclUf;

  const fromBaUf = inferNonFastchipBaTokenFromClientEstado(input.clientEstado);
  if (fromBaUf) return fromBaUf;

  const fromHdm2Uf = inferNonFastchipHdm2TokenFromClientEstado(input.clientEstado);
  if (fromHdm2Uf) return fromHdm2Uf;

  const fromEr2SpUf = inferNonFastchipEr2SpTokenFromClientEstado(input.clientEstado);
  if (fromEr2SpUf) return fromEr2SpUf;

  const fromDhUf = inferNonFastchipDhTokenFromClientEstado(input.clientEstado);
  if (fromDhUf) return fromDhUf;

  const fromHbCpUf = inferNonFastchipHbCpTokenFromClientEstado(input.clientEstado);
  if (fromHbCpUf) return fromHbCpUf;

  const seniorFromPhone = inferSeniorFromClientPhones(
    input.clientTelefone1,
    input.clientTelefone2
  );
  const fromPhone = mapSeniorTokenToNonFastchipOperational(seniorFromPhone);
  if (fromPhone) return fromPhone;

  return normalizeContractSaleTokenForForm(input.apiSuggestion, false);
}

function firstAditivoDdd(linhas?: FastchipClusterInferenceLine[]): string | null {
  const firstAditivo = linhas?.find(
    (l) => l.negociacao === 'Aditivo' && !l.excluirDoContrato && (l.ddd ?? '').trim()
  );
  return firstAditivo?.ddd?.trim() || null;
}

/**
 * Token Senior Fastchip para preencher o select automaticamente.
 * Ordem: UF cadastro (com conflito Aditivo > UF) → DDD telefones → sugestão API.
 */
export function resolveFastchipClusterTokenForAutoFill(
  input: ResolveFastchipClusterTokenInput
): string {
  const fromMarketUf = inferFastchipSeniorClusterTokenFromClientEstado(
    input.clientEstado,
    input.teamClusterPreference
  );
  const aditivoDdd = firstAditivoDdd(input.linhas);
  const fromAditivo = aditivoDdd
    ? inferFastchipSeniorClusterTokenFromDdd(aditivoDdd, undefined, input.teamClusterPreference)
    : null;

  if (fromAditivo && fromMarketUf && fromAditivo !== fromMarketUf) {
    return fromAditivo;
  }
  if (fromMarketUf) return fromMarketUf;
  if (fromAditivo) return fromAditivo;

  const ddd = resolveDddForFastchipClusterInference({
    ...input,
    linhas: undefined,
  });
  if (ddd) {
    const fromDdd = inferFastchipSeniorClusterTokenFromDdd(ddd, undefined, input.teamClusterPreference);
    if (fromDdd) return fromDdd;
  }

  const fromCurrent = mapNonFastchipOperationalToSeniorCanonical(input.currentFormToken);
  if (fromCurrent) return fromCurrent;

  return normalizeContractSaleTokenForForm(input.apiSuggestion, true);
}

/** Origem da sugestão Fastchip (espelha a árvore de `resolveFastchipClusterTokenForAutoFill`). */
export function resolveFastchipClusterSuggestionSource(
  input: ResolveFastchipClusterTokenInput
): ClusterSuggestionSource {
  const fromMarketUf = inferFastchipSeniorClusterTokenFromClientEstado(input.clientEstado);
  const aditivoDdd = firstAditivoDdd(input.linhas);
  const fromAditivo = aditivoDdd
    ? inferFastchipSeniorClusterTokenFromDdd(aditivoDdd)
    : null;

  if (fromAditivo && fromMarketUf && fromAditivo !== fromMarketUf) {
    return 'linha Aditivo';
  }
  if (fromMarketUf) return 'UF cadastro';
  if (fromAditivo) return 'linha Aditivo';

  const ddd = resolveDddForFastchipClusterInference({ ...input, linhas: undefined });
  if (ddd && inferFastchipSeniorClusterTokenFromDdd(ddd)) {
    return 'DDD cliente';
  }

  const api = normalizeContractSaleTokenForForm(input.apiSuggestion, true);
  if (api) return 'API Senior';
  return 'manual';
}

/** Origem da sugestão non-fastchip (espelha `resolveNonFastchipClusterTokenForAutoFill`). */
export function resolveNonFastchipClusterSuggestionSource(
  input: ResolveNonFastchipClusterTokenInput
): ClusterSuggestionSource {
  if (inferNonFastchipTpclTokenFromClientEstado(input.clientEstado)) {
    return 'UF cadastro';
  }
  if (inferNonFastchipBaTokenFromClientEstado(input.clientEstado)) {
    return 'UF cadastro';
  }
  if (inferNonFastchipHdm2TokenFromClientEstado(input.clientEstado)) {
    return 'UF cadastro';
  }
  if (inferNonFastchipEr2SpTokenFromClientEstado(input.clientEstado)) {
    return 'UF cadastro';
  }
  if (inferNonFastchipDhTokenFromClientEstado(input.clientEstado)) {
    return 'UF cadastro';
  }
  if (inferNonFastchipHbCpTokenFromClientEstado(input.clientEstado)) {
    return 'UF cadastro';
  }
  const seniorFromPhone = inferSeniorFromClientPhones(
    input.clientTelefone1,
    input.clientTelefone2
  );
  if (mapSeniorTokenToNonFastchipOperational(seniorFromPhone)) {
    return 'DDD cliente';
  }
  const api = normalizeContractSaleTokenForForm(input.apiSuggestion, false);
  if (api) return 'API Senior';
  return 'manual';
}
